import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/AdminLayout';
import axiosInstance from '../../lib/axiosInstance';
import { toast } from 'react-toastify';
import { MdArrowBack, MdSend, MdDone, MdAccessTime, MdChat, MdMessage } from 'react-icons/md';

const TicketDetail = () => {
    const { ticketNo } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyMessage, setReplyMessage] = useState('');
    const [sending, setSending] = useState(false);

    const fetchDetail = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/brand/support/tickets/${ticketNo}`);
            if (response.data.success) {
                setTicket(response.data.ticket);
                setReplies(response.data.replies);
            }
        } catch (error) {
            console.error('Error fetching ticket detail:', error);
            // toast.error('Failed to load inquiry history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
    }, [ticketNo]);

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) return;

        try {
            setSending(true);
            const response = await axiosInstance.post(`/brand/support/tickets/${ticketNo}/replies`, {
                message: replyMessage
            });
            if (response.data.success) {
                toast.success('Reply submitted successfully');
                setReplyMessage('');
                fetchDetail();
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            toast.error(error.response?.data?.message || 'Failed to submit response');
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="p-20 text-center animate-pulse">Scanning Archive...</div>;
    if (!ticket) return <div className="p-20 text-center bg-red-50 text-red-500 rounded-3xl m-8">Inquiry not found in support log.</div>;

    const topicPath = typeof ticket.topic_path === 'string' ? JSON.parse(ticket.topic_path) : (ticket.topic_path || []);

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8">
                <button 
                    onClick={() => navigate('/support')}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-900 transition font-black uppercase tracking-widest text-[10px] mb-8"
                >
                    <MdArrowBack size={16}/> All Inquiries
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Chat Column */}
                    <div className="lg:col-span-8 flex flex-col gap-10">
                        <div>
                             <div className="flex items-start justify-between mb-2">
                                <h1 className="text-4xl font-black text-[var(--text)] tracking-tighter">{ticketNo}</h1>
                                <span className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest leading-none ${
                                    ticket.status === 'open' ? 'bg-emerald-100 text-emerald-700' : ticket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-[var(--border)] text-[var(--muted)]'
                                } shadow-sm`}>
                                    {ticket.status.replace('_', ' ')}
                                </span>
                             </div>
                             <div className="flex items-center gap-3 text-[var(--muted)] font-bold uppercase tracking-widest text-[10px] bg-[var(--bg)] border border-[var(--border)] inline-flex px-4 py-2 rounded-2xl">
                                {topicPath.map((t, idx) => (
                                    <React.Fragment key={idx}>
                                        <span>{t.label}</span>
                                        {idx < topicPath.length - 1 && <span className="opacity-20 mx-1">/</span>}
                                    </React.Fragment>
                                ))}
                             </div>
                        </div>

                        {/* Original Query */}
                        <div className="bg-slate-900 text-slate-200 p-10 rounded-[48px] shadow-2xl shadow-slate-200 relative overflow-hidden group">
                           <div className="relative z-10">
                               <div className="flex items-center gap-3 mb-6">
                                  <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white backdrop-blur-md border border-white/10">
                                     <MdChat size={20}/>
                                  </div>
                                  <div className="flex flex-col">
                                     <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">SUBMITTED ON</span>
                                     <span className="text-xs font-bold">{new Date(ticket.created_at).toLocaleString()}</span>
                                  </div>
                               </div>
                               <p className="text-lg md:text-xl font-medium leading-relaxed italic text-white/90">"{ticket.comment}"</p>
                           </div>
                           <div className="absolute top-0 right-0 p-8 transform rotate-12 opacity-5 text-white pointer-events-none group-hover:scale-125 transition duration-1000">
                               <MdMessage size={160}/>
                           </div>
                        </div>

                        {/* Thread */}
                        <div className="flex flex-col gap-6 ml-4 md:ml-10 border-l-[3px] border-slate-100 pl-8 md:pl-12 py-4">
                            {replies.length === 0 ? (
                                <div className="text-slate-300 font-bold uppercase tracking-widest text-xs py-8">Waiting for administrator response...</div>
                            ) : (
                                replies.map((reply) => (
                                    <div key={reply.id} className="relative">
                                        <div className="absolute top-0 -left-[43px] md:-left-[59px] w-6 h-6 rounded-full bg-[var(--surface)] border-[3px] border-[var(--border)] flex items-center justify-center translate-y-1">
                                            {reply.sender_type === 'admin' ? <MdDone size={10} className="text-blue-500"/> : <div className="w-2 h-2 rounded-full bg-[var(--muted)]"></div>}
                                        </div>
                                        <div className={`p-8 rounded-[36px] max-w-[90%] shadow-md shadow-slate-100/30 transition-all ${
                                            reply.sender_type === 'admin' 
                                            ? 'bg-blue-600 text-white rounded-tl-none font-medium' 
                                            : 'bg-[var(--surface)] border-2 border-[var(--border)] text-[var(--text)] rounded-tr-none'
                                        }`}>
                                            <div className="flex items-center gap-3 mb-4">
                                                <span className={`text-[10px] font-black uppercase tracking-widest ${reply.sender_type === 'admin' ? 'text-white/60' : 'text-[var(--muted)]'}`}>
                                                    {reply.sender_type === 'admin' ? 'Official Support' : 'Your Response'}
                                                </span>
                                                <span className={`text-[10px] opacity-40 italic ${reply.sender_type === 'admin' ? 'text-white' : 'text-[var(--muted)]'}`}>
                                                    {new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            <p className="md:text-lg leading-relaxed">{reply.message}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Quick Response */}
                        {ticket.status !== 'closed' && (
                            <div className="bg-[var(--surface)] p-4 md:p-6 rounded-[48px] border-2 border-[var(--border)] shadow-xl shadow-slate-100/20 group focus-within:border-blue-500 transition-all duration-500">
                                <form onSubmit={handleReply}>
                                    <textarea
                                        className="w-full bg-transparent border-0 focus:ring-0 p-6 text-lg h-32 md:h-40 resize-none outline-none font-medium placeholder:text-[var(--muted)] text-[var(--text)]"
                                        placeholder="Add a follow-up response..."
                                        value={replyMessage}
                                        onChange={(e) => setReplyMessage(e.target.value)}
                                    />
                                    <div className="flex justify-end p-2">
                                        <button
                                            type="submit"
                                            disabled={sending || !replyMessage.trim()}
                                            className="bg-slate-900 text-white px-10 py-5 rounded-[32px] font-black flex items-center gap-3 hover:bg-blue-600 active:scale-95 transition-all shadow-xl shadow-slate-200 disabled:opacity-50"
                                        >
                                            {sending ? 'Submitting...' : <><MdSend size={20}/> Submit Response</>}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>

                    {/* Meta Sidebar */}
                    <div className="lg:col-span-4 flex flex-col gap-8">
                         <div className="bg-slate-100/50 p-10 rounded-[48px] border border-slate-200/50 backdrop-blur-xl">
                             <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-8">Metadata Analysis</h4>
                             
                             <div className="space-y-10">
                                 <div className="flex items-center gap-5">
                                     <div className="w-14 h-14 bg-[var(--surface)] rounded-2xl flex items-center justify-center text-[var(--muted)] shadow-sm border border-[var(--border)]">
                                        <MdAccessTime size={24}/>
                                     </div>
                                     <div>
                                         <span className="text-[10px] font-black text-[var(--muted)] uppercase tracking-widest block mb-1">Time Elapsed</span>
                                         <span className="text-sm font-black text-[var(--text)]">
                                             {Math.floor((Date.now() - new Date(ticket.created_at).getTime()) / (1000 * 60 * 60 * 24))} Days Active
                                         </span>
                                     </div>
                                 </div>

                                 <div className="flex items-center gap-5">
                                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm border ${
                                         ticket.priority === 'high' ? 'bg-red-50 border-red-100 text-red-500' : 'bg-white border-slate-100 text-slate-400'
                                     }`}>
                                        <MdDone size={24}/>
                                     </div>
                                     <div>
                                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Impact Level</span>
                                         <span className={`text-sm font-black uppercase ${ticket.priority === 'high' ? 'text-red-600' : 'text-slate-800'}`}>
                                            {ticket.priority} Urgency
                                         </span>
                                     </div>
                                 </div>
                             </div>

                             <div className="mt-12 pt-8 border-t border-slate-200/50">
                                 <div className="p-6 bg-slate-900 rounded-3xl text-white">
                                     <p className="text-[11px] font-bold leading-relaxed opacity-60 italic whitespace-pre-line">
                                         Official inquiry records are maintained under security protocol. Contact administrator for log exports.
                                     </p>
                                 </div>
                             </div>
                         </div>
                    </div>
                </div>
            </div>
    );
};

export default TicketDetail;
